import { useCallback } from 'react';
import { useApolloClient, useMutation } from '@apollo/client/react';
import { toast } from 'react-toastify';
import { useIntl } from 'react-intl';
import {
  type TransferProjectMutationVariables,
  TransferProjectDocument,
  LastProjectDocument,
  ProjectsDocument,
  type ProjectsQuery,
  type ProjectsQueryVariables,
} from 'graphql/generated/graphql';
import requireValue from 'utils/requireValue';
import { toGraphqlError, withRaw } from 'utils/error';

const useTransferProject = () => {
  const [transferProjectMutation] = useMutation(TransferProjectDocument);
  const client = useApolloClient();
  const { formatMessage: t } = useIntl();

  const handleSubmit = useCallback(
    async (variables: TransferProjectMutationVariables) => {
      try {
        await transferProjectMutation({
          variables,
          refetchQueries: [{ query: LastProjectDocument }],
          awaitRefetchQueries: true,
        });
        const { projects } = requireValue(
          client.readQuery<ProjectsQuery, ProjectsQueryVariables>({ query: ProjectsDocument }),
          'projects',
        );
        const filteredProjects = projects.filter(
          (project) => requireValue(project, '_id')._id !== variables.projectId,
        );

        client.writeQuery<ProjectsQuery, ProjectsQueryVariables>({
          query: ProjectsDocument,
          data: { projects: filteredProjects },
        });
        toast.success(t({ id: 'my_projects.successfully_transferred' }));
      } catch (error) {
        throw withRaw(toGraphqlError(error), error || '');
      }
    },
    [client, t, transferProjectMutation],
  );
  return handleSubmit;
};

export default useTransferProject;
